<script lang="ts" setup>
import { computed, onMounted, PropType, ref, watch } from 'vue'
import { TirPmDraggable, DragModel } from './'
import { useRootFontSize } from 'tir-pm-toolkit';

/**Параметры компонента */
const props = defineProps({
  /**Активен вертикальный режим */
  vertical: { type: Boolean },
  /**Выкл/вкл перетаскивания */
  enabled: { default: true },
  /**Элементы для сортировки */
  data: { default: null, type: Object as PropType<DragModel<any>[]> },
  /**Функция для разрешения дропа */
  allowDropFunc: { default: null, type: Function },
  /**Поле значения (Можно использовать длинные ключи(object.object2.key)) */
  valueField: { default: '' },
  /**Длительность анимации при драге */
  animationDuration: { default: 0.2 },
  /**Велечина отступов */
  indent: { default: 5 },
  /**Использовать ли анимацию */
  isTransition: { default: false },
  /**Позволить скроллить во время драга */
  isAllowScroll: { default: false, type: Boolean },
  /**Скорость скролла при касании */
  dragScrollSpeed: { default: 10, type: Number },
  /**Класс для обертки */
  dataClass: { default: '', type: String },
  /**Включение задержки перед драгом */
  isAllowClickDelay: { default: false, type: Boolean },
  /**Отключить боковые отступы */
  disableSideIndent: { default: false, type: Boolean },
  /**Перетаскивается ли блок вне компонента */
  outerIsDrag: { default: false, type: Boolean },
  /**Флаг для очистки данных о перетаскиваемом элементе */
  cleanFlag: { default: false, type: Boolean },
  /**Отображение пустой область для дропа */
  isAllowPlug: { default: false, type: Boolean },
  /**Внешний перетаскиваемый элемент */
  outerCurrentDragging: {
    default: null,
    type: Object as PropType<DragModel<any>>,
  },
  /**DOM элемент области прокрутки */
  scrollArea: { default: null, type: HTMLElement },
  /**Пропускать выход элементов за границы */
  skipEdge: { default: false, type: Boolean },
})
/**События компонента */
const emit = defineEmits(['change', 'added', 'dragComponentOnDrag', 'cancel'])

const { rootFontSizePx } = useRootFontSize();
/**Данные о списке */
const innerData = ref<DragModel<any>[]>([])
/**Текущий перетаскиваемый компонент */
const currentDragging = ref<DragModel<any>>()
/**Перетаскивается ли элемент */
const isDrag = ref<boolean>(false)
/**Элемент над которым находится перетаскиваемый */
const currentDragOver = ref<number | null>()
/**Index перетаскиваемого элемента */
const currentDraggingIndex = ref<number | null>()
/**Ширина/Высота перетаскиваемого элемента */
const draggingElSize = ref<number>()
/**Модель для drop обертки списка */
const wrapperDragModel = ref<DragModel<any>>(new DragModel())
/**DOM элемент предложения */
const $sentence = ref<HTMLDivElement[]>()

/**Атрибут name элемента transition-group */
const transitionGroupName = computed(() =>
  props.isTransition ? 'flip-list' : ''
)
/**Стиль drag обертки компонента */
const dragWrapperStyle = computed(() => ({
  minHeight: props.isAllowPlug && !innerData.value.length ? '100%' : '',
}))
/**Классы drag обертки компонента */
const dragWrapperClasses = computed(() => ({
  vertical: props.vertical,
}))
/**Поле для отслеживания изменений параметра data */
const data = computed(() => props.data)
/**Поле для отслеживания изменений параметра cleanFlag */
const cleanFlag = computed(() => props.cleanFlag)

/**Отслеживание изменений параметра data */
watch(data, onUpdateData, { deep: true })
/**Отслеживание изменений параметра cleanFlag */
watch(cleanFlag, onUpdateCleanFlag, { deep: true })

/**После рендера компонента */
onMounted(() => {
  onUpdateData()
  wrapperDragModel.value.AllowDrop = true
})

/**Стиль drag-component элемента списка */
const dragComponentItemStyle = computed(() => {
  const marginStyle = props.vertical
    ? `${props.indent / rootFontSizePx.value}rem 0rem`
    : `0rem ${props.indent / rootFontSizePx.value}rem`
  return {
    margin: !props.disableSideIndent ? marginStyle : '0rem',
  }
})
/**Получение стилей для обертки элемента списка */
const getDragItemWrapperStyle = (index: number) => ({
  opacity:
    currentDraggingIndex.value == index && (isDrag.value || props.outerIsDrag)
      ? '0'
      : '',
})
/**Получение стилей для элемента списка */
const getDragItemStyle = (index: number) => {
  const isIndexesValid =
    typeof currentDragOver.value == 'number' &&
    typeof currentDraggingIndex.value == 'number'

  const overIndex = currentDragOver.value as number

  const indentSize = props.indent / rootFontSizePx.value + 'rem'
  const marginStyle = props.vertical
    ? `0rem ${indentSize}`
    : `${indentSize} 0rem`

  const style: any = {
    margin: currentDraggingIndex.value == null ? marginStyle : '',
    transform: onTransform(index),
  }

  if (currentDraggingIndex.value != index) {
    style.transition =
      currentDraggingIndex.value != null
        ? `transform ${props.animationDuration}s`
        : ''
  }

  if (props.vertical) {
    style.margin = `0 ${indentSize} !important`
  } else {
    style.paddingTop =
      isIndexesValid && (index <= overIndex || index == currentDragOver.value)
        ? indentSize
        : ''
    style.paddingBottom =
      isIndexesValid && (index >= overIndex || index == currentDragOver.value)
        ? indentSize
        : ''
  }

  return style
}
/**Очистка данных о перетаскиваемом элементе */
function onUpdateCleanFlag() {
  currentDragOver.value = null
  currentDraggingIndex.value = null
  currentDragging.value = undefined
}
/**Заполнение поля innerData */
function onUpdateData() {
  innerData.value = props.data
}
/**Получение значения объекта */
const getValue = (index: number) => {
  let value: any = innerData.value[index]?.Data
  if (props.valueField.length) {
    let keys = props.valueField.split('.')
    for (let i = 0; i < keys.length; i++) {
      value = value[keys[i]]
    }
    return value
  } else {
    return innerData.value[index]?.Data
  }
}
/**Получение transform стиля */
const onTransform = (index: number) => {
  if (
    typeof currentDraggingIndex.value != 'number' ||
    typeof currentDragOver.value != 'number'
  )
    return ''

  const value = (Number(draggingElSize.value) - props.indent * 2) / rootFontSizePx.value
  const direction = props.vertical ? 'X' : 'Y'

  if (currentDraggingIndex.value == index) {
    return ''
  }
  if (currentDraggingIndex.value <= index && index <= currentDragOver.value) {
    return `translate${direction}(-${value}rem)`
  }
  if (currentDraggingIndex.value >= index && index >= currentDragOver.value) {
    return `translate${direction}(${value}rem)`
  }
}
/**Получение высоты перетаскиваемого элемента, при старте перетаскивания */
const sentenceOnMouseDown = (event: any) => {
  let rect = (event.currentTarget as HTMLElement)?.getBoundingClientRect()
  draggingElSize.value = props.vertical ? rect?.width : rect?.height
}
const sentenceOnTouchStart = (event: TouchEvent) => {
  sentenceOnMouseDown(event.touches[0])
}
/**Перетаскивание элемента */
const dragComponentOnDrag = (isDragging: boolean, model: DragModel<any>) => {
  isDrag.value = isDragging
  if (!isDrag.value) {
    onUpdateCleanFlag()
  } else {
    let index = innerData.value.findIndex((x) => x == model)
    currentDragging.value = model
    currentDraggingIndex.value = index
    if (currentDragOver.value == null)
      currentDragOver.value = currentDraggingIndex.value
  }
  emit('dragComponentOnDrag', isDrag.value, model)
}

/**Поиск index'a элемента, над которым перетаскивается элемент */
const dragComponentOnOver = (isOver: boolean, model: DragModel<any>) => {
  if ((props.outerIsDrag || isDrag.value) && isOver)
    currentDragOver.value = innerData.value.findIndex((x) => x == model)
}
/**Обработка перетаскивания внешнего компонента над текущим */
const dragWrapperOnOver = (isOver: boolean) => {
  if (!props.outerCurrentDragging) return
  if (props.outerCurrentDragging == currentDragging.value) {
    if (!isOver) currentDragOver.value = null
    return
  }
  if (!isOver || !props.outerCurrentDragging) {
    innerData.value = innerData.value.filter(
      (x) => x != props.outerCurrentDragging
    )
    emit('cancel', innerData.value)
    currentDragOver.value = null
    currentDraggingIndex.value = null
  } else if (
    !innerData.value.find((x) => x.Data == props.outerCurrentDragging.Data)
  ) {
    innerData.value.push(props.outerCurrentDragging)
    currentDraggingIndex.value = innerData.value.length - 1
    setTimeout(() => {
      if (!currentDraggingIndex.value) return
      let rect =
        $sentence.value?.[currentDraggingIndex.value]?.getBoundingClientRect()
      if (rect) draggingElSize.value = props.vertical ? rect.width : rect.height
    })
  }
}
/**Сброс слова в список */
const dragComponentOnDrop = (model: DragModel<any>) => {
  let target: DragModel<any> | null = null
  let sourceIndex: number | null = null
  let targetIndex: number | null = null

  targetIndex = innerData.value.findIndex((x) => x == model)

  target = innerData.value[targetIndex]

  if (target && currentDragging.value && isDrag.value) {
    sourceIndex = innerData.value.findIndex((x) => x == currentDragging.value)

    if (sourceIndex == targetIndex) return

    if (sourceIndex > targetIndex) {
      for (let i = sourceIndex; i > targetIndex; i--) {
        exchangeItems(innerData.value, i, i - 1)
      }
    } else {
      for (let i = sourceIndex; i < targetIndex; i++) {
        exchangeItems(innerData.value, i, i + 1)
      }
    }
    emit('change', innerData.value)
  } else if (props.outerCurrentDragging && props.outerIsDrag) {
    innerData.value = innerData.value.filter(
      (x) => x != props.outerCurrentDragging
    )
    innerData.value.splice(targetIndex, 0, props.outerCurrentDragging)
    emit('added', innerData.value)
  }
  onUpdateCleanFlag()
}
/**При дропе в заглушку */
const dragWrapperOnDrop = () => {
  if (!props.isAllowPlug) return
  if (props.outerCurrentDragging && props.outerIsDrag) {
    innerData.value = innerData.value.filter(
      (x) => x != props.outerCurrentDragging
    )
    innerData.value.push(props.outerCurrentDragging)
    emit('added', innerData.value)
  }
  onUpdateCleanFlag()
}
/**Перемещение элементов массива */
const exchangeItems = (
  array: any[],
  firstItemIndex: number,
  secondItemIndex: number
) => {
  array[firstItemIndex] = [
    array[secondItemIndex],
    (array[secondItemIndex] = array[firstItemIndex]),
  ][0]
  return array
}
</script>
<template>
  <div v-if="innerData?.length" class="sorting_wrapper">
    <TirPmDraggable
      class="drag_wrapper"
      :dragModel="wrapperDragModel"
      :style="dragWrapperStyle"
      :class="dragWrapperClasses"
      @over="dragWrapperOnOver"
      @drop="dragWrapperOnDrop"
    >
      <TransitionGroup :name="transitionGroupName">
        <div
          v-for="(item, index) in innerData"
          :key="item.Data?.TaskId || index"
          ref="$sentence"
          :style="getDragItemWrapperStyle(index)"
          @mousedown="sentenceOnMouseDown"
          @touchstart="sentenceOnTouchStart"
        >
          <TirPmDraggable
            class="drag_component"
            :dragModel="item"
            :scrollArea="scrollArea"
            :allowDropFunc="allowDropFunc"
            :isAllowScroll="isAllowScroll"
            :dragScrollSpeed="dragScrollSpeed"
            :style="dragComponentItemStyle"
            :isAllowClickDelay="isAllowClickDelay"
            :enabled="enabled"
            :skipEdge="skipEdge"
            :click-delay="150"
            @dragging="dragComponentOnDrag"
            @over="dragComponentOnOver"
            @drop="dragComponentOnDrop"
          >
            <div
              class="data"
              :class="dataClass"
              :style="getDragItemStyle(index)"
            >
              <slot name="item" :item="getValue(index)" :index="index" />
            </div>
          </TirPmDraggable>
        </div>
      </TransitionGroup>
    </TirPmDraggable>
  </div>
</template>
<style lang="scss" scoped>
.sorting_wrapper {
  height: 100%;
}

.drag_component:not(.disabled) {
  cursor: grab;
}
</style>
<style lang="scss">
.flip-list-move {
  transition: transform 0.2s;
}

.data.dragging {
  margin: 0 !important;
}
.drag_wrapper.vertical {
  display: flex;
}
</style>
